/**
 * La **pilule de filtre** — le déclencheur partagé des deux contrôles de
 * filtre du back-office, et la forme que la maquette leur donne.
 *
 * Maquette : le gabarit `boFilters` de
 * `designs/client-app/project/Jëliko - Guichet, Back-office, Marchand.dc.html`.
 * Le dessin n'y met **jamais un champ de saisie** : chaque filtre est une
 * pilule bordée qui porte son libellé en petit, sa valeur en gras, et un
 * chevron. Le porteur l'a vu du premier coup d'œil dans Chrome — deux écrans
 * livrés offraient une zone de texte nue à la place.
 *
 * ## Écrit UNE fois, pour les deux
 *
 * `FilterSelect` et `FilterDateField` partagent ce déclencheur au caractère
 * près. Deux pilules recopiées auraient dérivé — c'est exactement la
 * divergence que ce dépôt paie ailleurs — et la rangée de filtres aurait cessé
 * de se lire comme une rangée. Ce qui les distingue tient dans trois attributs
 * ARIA, passés en paramètre : `role`, `aria-haspopup`, `aria-activedescendant`.
 *
 * ## Ni `<select>`, ni `<input type="date">`
 *
 * Les deux contrôles natifs peignent l'interface du **système**, pas celle du
 * produit : la liste déroulante de macOS ignore les jetons, et le calendrier du
 * navigateur a ses propres couleurs et ses propres rayons. À côté d'une pilule
 * dessinée, ils se voient. Ce qu'ils donnaient gratuitement — le clavier,
 * l'annonce, les états — est donc repris à la main, et **éprouvé** : une liste
 * qui ne marcherait qu'à la souris serait une régression contre la zone de
 * texte qu'elle remplace, puisque celle-là marchait au clavier.
 *
 * ## Les quatre garanties, posées ici et pas ailleurs
 *
 *  1. **le libellé est un vrai `<label for>`** — on peut cliquer dessus pour
 *     atteindre le déclencheur — **et** la première moitié du nom accessible ;
 *  2. **le nom accessible porte la valeur** : `aria-labelledby` désigne le
 *     libellé *puis* la valeur affichée. Un déclencheur qui s'annoncerait
 *     « Journée comptable, bouton » sans dire quelle journée obligerait à
 *     l'ouvrir pour le savoir ;
 *  3. **l'état d'ouverture est annoncé** — `aria-expanded`, et `aria-controls`
 *     vers le panneau tant qu'il est ouvert ;
 *  4. **le chevron est décoratif** — `aria-hidden`. Il redit ce que le rôle
 *     annonce déjà ; laissé visible, il ferait lire un caractère de plus par
 *     champ.
 */

import type { KeyboardEventHandler, ReactNode, RefObject } from 'react';

import { useTranslation } from 'react-i18next';

/** Ce que la pilule attend. */
export interface FilterPillProps {
  /** L'identifiant du déclencheur — c'est lui que le libellé désigne. */
  readonly triggerId: string;
  /** L'identifiant du libellé, première moitié du nom accessible. */
  readonly labelId: string;
  /** L'identifiant de la valeur affichée, seconde moitié du nom accessible. */
  readonly valueId: string;
  /** Clé de traduction du libellé. Aucune chaîne d'écran n'est écrite ici. */
  readonly labelKey: string;
  /** Ce que l'opérateur LIT — jamais ce qui part vers la route. */
  readonly display: string;
  /** L'identifiant du panneau, pour `aria-controls`. */
  readonly panelId: string;
  /** Le panneau est-il ouvert ? */
  readonly expanded: boolean;
  /** La nature du panneau : une liste, ou une boîte de dialogue. */
  readonly haspopup: 'listbox' | 'dialog';
  /** Le rôle du déclencheur. `combobox` pour une liste ; un bouton sinon. */
  readonly role?: 'combobox';
  /** L'option active d'une liste, suivie **sans déplacer le focus**. */
  readonly activeDescendant?: string;
  /** Le champ est-il en erreur ? */
  readonly invalid?: boolean;
  /** L'identifiant du message d'erreur, pour `aria-describedby`. */
  readonly describedBy?: string;
  /** Le déclencheur, pour lui rendre le focus à la fermeture. */
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  /** Le contenant, pour détecter le clic au dehors. */
  readonly containerRef: RefObject<HTMLSpanElement | null>;
  /** Ouvre ou ferme. */
  readonly onToggle: () => void;
  /** Le clavier du déclencheur — propre à chaque contrôle. */
  readonly onKeyDown: KeyboardEventHandler<HTMLButtonElement>;
  /** Le panneau, quand il est ouvert. */
  readonly children?: ReactNode;
}

/** La pilule du dessin, et le déclencheur qu'elle habille. */
export function FilterPill({
  triggerId,
  labelId,
  valueId,
  labelKey,
  display,
  panelId,
  expanded,
  haspopup,
  role,
  activeDescendant,
  invalid,
  describedBy,
  triggerRef,
  containerRef,
  onToggle,
  onKeyDown,
  children,
}: FilterPillProps): ReactNode {
  const { t } = useTranslation();
  return (
    <span className="filter-pill" ref={containerRef}>
      <label className="filter-pill__label" id={labelId} htmlFor={triggerId}>
        {t(labelKey)}
      </label>
      <button
        className="filter-pill__value"
        id={triggerId}
        ref={triggerRef}
        type="button"
        role={role}
        aria-haspopup={haspopup}
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        aria-activedescendant={expanded ? activeDescendant : undefined}
        aria-labelledby={`${labelId} ${valueId}`}
        aria-invalid={invalid === true ? true : undefined}
        aria-describedby={describedBy}
        onClick={onToggle}
        onKeyDown={onKeyDown}
      >
        <span id={valueId}>{display}</span>
      </button>
      <span className="filter-pill__chevron" aria-hidden="true">
        ▾
      </span>
      {children}
    </span>
  );
}
